import {
  applyFilterState,
  PartlistFilterableField,
  SamFilterType,
  selectPartlistFilterTypes,
  selectSamPartlistFilters,
} from "@org-avp/avp-avengers-ui-shared-store";
import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";

export interface PartlistEntryPartKey {
  partlistEntryId: string;
  bomData: {
    partKey: string;
  };
}

interface PartlistEntryMarks {
  mark1: boolean;
  mark2: boolean;
}

interface PartlistEntryFilterStateHelper<T extends PartlistEntryPartKey> {
  addMarks: (partlistEntryPartKey: T) => T & PartlistEntryMarks;
  filterByMarks: (markedEntries: (T & PartlistEntryMarks)[]) => (T & PartlistEntryMarks)[];
  isFilterActive: (type: SamFilterType) => boolean;
  passesMarksFilter: (partlistEntryMarks: PartlistEntryMarks) => boolean;
}

export const usePartlistEntryFilterStateHelper = <T extends PartlistEntryPartKey>(): PartlistEntryFilterStateHelper<T> => {
  const partlistFilterState = useSelector(selectSamPartlistFilters);
  const partlistFilterTypes = useSelector(selectPartlistFilterTypes);

  const applyPartlistEntryFilterState = useCallback(
    (filterTypes: SamFilterType[], partlistEntry?: T) => {
      return applyFilterState(partlistFilterState, filterTypes, (field: string) => {
        switch (field) {
          case PartlistFilterableField.PARTLIST_ENTRY_ID:
            return partlistEntry?.partlistEntryId;
          case PartlistFilterableField.BOM_BASE_NUMBER:
            return partlistEntry?.bomData?.partKey?.substring(4, 11);
          case PartlistFilterableField.BOM_PART_KEY:
            return partlistEntry?.bomData?.partKey;
        }

        console.error("Field not supported on partlist: ", field);
        return null;
      });
    },
    [partlistFilterState],
  );

  const addMarks: (partlistEntry: T) => (T & PartlistEntryMarks) = useCallback(
    (partlistEntry: T): T & PartlistEntryMarks => ({
      mark1: applyPartlistEntryFilterState([SamFilterType.MARK_1], partlistEntry),
      mark2: applyPartlistEntryFilterState([SamFilterType.MARK_2], partlistEntry),
      ...partlistEntry,
    }),
    [applyPartlistEntryFilterState],
  );

  const isFilterActive = useCallback(
    (type: SamFilterType) => partlistFilterTypes.includes(type),
    [partlistFilterTypes],
  );

  const passesMarksFilter: (partlistEntryMarks: PartlistEntryMarks) => boolean = useCallback(
    ({ mark1, mark2 }): boolean => {
      const filterByMark1 = !isFilterActive(SamFilterType.MARK_1) || mark1;
      const filterByMark2 = !isFilterActive(SamFilterType.MARK_2) || mark2;
      return filterByMark1 && filterByMark2;
    },
    [isFilterActive],
  );

  const filterByMarks: (markedEntries: (T & PartlistEntryMarks)[]) => (T & PartlistEntryMarks)[] = useCallback(
    (markedEntries: (T & PartlistEntryMarks)[]) => {
      return markedEntries.filter(passesMarksFilter);
    },
    [passesMarksFilter],
  );

  return useMemo(
    () => ({
      addMarks,
      filterByMarks,
      isFilterActive,
      passesMarksFilter,
    }),
    [addMarks, filterByMarks, isFilterActive, passesMarksFilter],
  );
};
